import React from 'react';
import { 
    Home, Save, Trash2, Upload, X, 
    Package, Store, ShoppingBag, Briefcase, Factory, Coffee, 
    Utensils, Wrench, Hammer, Globe, Truck, Anchor, Zap, Crown, Camera,
    Image as ImageIcon, AlertCircle
} from 'lucide-react';

const Card = ({ children, className = "", noPadding = false }) => (
  <div className={`bg-white rounded-3xl border border-slate-100 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all duration-300 ${className}`}>
    {noPadding ? children : <div className="p-6 md:p-8">{children}</div>}
  </div>
);

const Input = ({ label, className, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <input 
        className={`
          w-full px-4 py-3 rounded-xl border border-slate-200 bg-white
          focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none
          transition-all duration-200 text-slate-700 placeholder:text-slate-400
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          ${className || ''}
        `}
        {...props}
      />
    </div>
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div className="flex flex-col gap-2 w-full">
    {label && (
      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 ml-1">
        {label} {props.required && <span className="text-rose-500">*</span>}
      </label>
    )}
    <div className="relative">
      <select 
        className={`
          w-full px-4 py-3 rounded-xl border border-slate-200 bg-white
          focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none
          transition-all duration-200 text-slate-700
          disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
          appearance-none
        `}
        {...props}
      >
        {children}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  </div>
);

const Button = ({ children, onClick, variant = "primary", className = "", disabled = false, icon: Icon, fullWidth = false, ...props }) => {
  const variants = {
    primary: "bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/30",
    secondary: "bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-sm",
    danger: "bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/30",
    ghost: "bg-transparent hover:bg-slate-100 text-slate-600",
  };
  
  return (
    <button 
      onClick={onClick} 
      disabled={disabled}
      className={`
        inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] touch-manipulation
        disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
        ${variants[variant]} 
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon size={18} className={disabled ? 'animate-spin' : ''} />}
      {children}
    </button>
  );
};

/**
 * Project Details Component
 * Handles company details, warehouse info, and project identity settings
 */
const ProjectDetails = React.memo(({
    activeProject,
    selectedProjectIcon,
    setSelectedProjectIcon,
    setConfirmModal,
    db,
    fetchData,
    showToast,
    requestConfirm,
    can,
    setIsDeleteProjectModalOpen,
    setDeleteProjectPassword,
    setDeleteProjectError
}) => {
    return (
        <div className="space-y-6 max-w-4xl">
            {/* Butiran Syarikat (Company Details) */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1">Butiran Syarikat</h3>
                    <p className="text-sm text-slate-500">Akan dipaparkan pada invois dan PO.</p>
                </div>
                <form id="projectDetailsForm" className="space-y-6">
                    <Input 
                        label="NAMA SYARIKAT BERDAFTAR" 
                        name="companyName"
                        defaultValue={activeProject.companyName} 
                        placeholder="Urban Kitchen Sdn Bhd"
                    />
                    
                    {/* Company Logo Upload */}
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                            LOGO SYARIKAT
                        </label>
                        <p className="text-xs text-slate-500 mb-3">Logo akan dipaparkan pada PDF PO dan Laporan Audit Stok.</p>
                        <div className="flex items-start gap-4">
                            {/* Logo Preview */}
                            <div className="flex-shrink-0">
                                {activeProject.companyLogo ? (
                                    <div className="relative w-32 h-32 border-2 border-slate-200 rounded-xl overflow-hidden bg-white">
                                        <img 
                                            src={activeProject.companyLogo} 
                                            alt="Company Logo" 
                                            className="w-full h-full object-contain p-2"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setConfirmModal({
                                                    isOpen: true,
                                                    title: 'Padam Logo Syarikat',
                                                    message: 'Adakah anda pasti mahu memadam logo syarikat ini?',
                                                    onConfirm: async () => {
                                                        try {
                                                            await db.updateProject(activeProject.id, { companyLogo: '' });
                                                            fetchData();
                                                            showToast('Logo syarikat dipadam');
                                                        } catch (err) {
                                                            showToast('Gagal memadam logo', 'error');
                                                        }
                                                        setConfirmModal({ isOpen: false });
                                                    }
                                                });
                                            }}
                                            className="absolute top-1 right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600 transition-colors"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-slate-50">
                                        <ImageIcon size={32} className="text-slate-400" />
                                    </div>
                                )}
                            </div>
                            
                            {/* Upload Button */}
                            <div className="flex-1">
                                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 cursor-pointer transition-all">
                                    <Upload size={16} />
                                    {activeProject.companyLogo ? 'Tukar Logo' : 'Muat Naik Logo'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                if (file.size > 2 * 1024 * 1024) {
                                                    showToast('Saiz gambar terlalu besar. Maksimum 2MB.', 'error');
                                                    return;
                                                }
                                                const reader = new FileReader();
                                                reader.onloadend = async () => {
                                                    try {
                                                        await db.updateProject(activeProject.id, { companyLogo: reader.result });
                                                        fetchData();
                                                        showToast('Logo syarikat dikemas kini');
                                                    } catch (err) {
                                                        showToast('Gagal muat naik logo', 'error');
                                                    }
                                                };
                                                reader.readAsDataURL(file);
                                            }
                                        }}
                                    />
                                </label>
                                <p className="text-xs text-slate-500 mt-2">Format: PNG, JPG, GIF (Maks 2MB)</p>
                                <p className="text-xs text-slate-500">Disyorkan: 200x200px atau lebih tinggi</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="NO. SSM SYARIKAT" 
                            name="companySsm"
                            defaultValue={activeProject.companySsm} 
                            placeholder="202301045123"
                        />
                        <Input 
                            label="NO. TELEFON RASMI" 
                            name="companyPhone"
                            defaultValue={activeProject.companyPhone} 
                            placeholder="03-xxxxxxxx"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="EMEL RASMI" 
                            name="companyEmail"
                            type="email"
                            defaultValue={activeProject.companyEmail} 
                            placeholder="info@syarikat.com"
                        />
                        <Input 
                            label="LAMAN WEB" 
                            name="companyWebsite"
                            defaultValue={activeProject.companyWebsite} 
                            placeholder="www.syarikat.com"
                        />
                    </div>
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">ALAMAT SURAT MENYURAT (SYARIKAT)</label>
                        <Input 
                            name="companyAddressLine1"
                            defaultValue={activeProject.companyAddressLine1} 
                            placeholder="Level 10, Menara Urban, Jalan Tun Razak, 50400, W.P. Kuala Lumpur"
                        />
                        <Input 
                            name="companyAddressLine2"
                            defaultValue={activeProject.companyAddressLine2} 
                            placeholder="Baris 2 (Pilihan)"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                name="companyPostcode"
                                defaultValue={activeProject.companyPostcode} 
                                placeholder="Poskod"
                            />
                            <Select 
                                name="companyState"
                                defaultValue={activeProject.companyState || 'W.P. Kuala Lumpur'}
                            >
                                <option value="Johor">Johor</option>
                                <option value="Kedah">Kedah</option>
                                <option value="Kelantan">Kelantan</option>
                                <option value="Melaka">Melaka</option>
                                <option value="Negeri Sembilan">Negeri Sembilan</option>
                                <option value="Pahang">Pahang</option>
                                <option value="Penang">Penang</option>
                                <option value="Perak">Perak</option>
                                <option value="Perlis">Perlis</option>
                                <option value="Sabah">Sabah</option>
                                <option value="Sarawak">Sarawak</option>
                                <option value="Selangor">Selangor</option>
                                <option value="Terengganu">Terengganu</option>
                                <option value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</option>
                                <option value="W.P. Labuan">W.P. Labuan</option>
                                <option value="W.P. Putrajaya">W.P. Putrajaya</option>
                            </Select>
                        </div>
                    </div>
                </form>
            </Card>

            {/* Maklumat Gudang (Warehouse Information) */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1 flex items-center gap-2">
                        <Home size={20} className="text-emerald-600" />
                        Maklumat Gudang
                    </h3>
                    <p className="text-sm text-slate-500">Nama dan alamat lokasi operasi/penghantaran.</p>
                </div>
                <div className="space-y-6">
                    <Input 
                        label="NAMA GUDANG" 
                        name="warehouseName"
                        form="projectDetailsForm"
                        defaultValue={activeProject.warehouseName || activeProject.warehouse} 
                        placeholder="Stor Cawangan Bangsar"
                        required
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input 
                            label="NAMA PIC GUDANG" 
                            name="warehousePicName"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehousePicName} 
                            placeholder="Cik Sarah"
                        />
                        <Input 
                            label="NO. TELEFON PIC" 
                            name="warehousePicPhone"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehousePicPhone} 
                            placeholder="012-9988776"
                        />
                    </div>
                    <Input 
                        label="EMEL PIC" 
                        name="warehousePicEmail"
                        type="email"
                        form="projectDetailsForm"
                        defaultValue={activeProject.warehousePicEmail} 
                        placeholder="sarah@urbancafe.demo"
                    />
                    <div className="space-y-4">
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider">ALAMAT GUDANG (PENGHANTARAN)</label>
                        <Input 
                            name="warehouseAddressLine1"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehouseAddressLine1} 
                            placeholder="Baris 1"
                        />
                        <Input 
                            name="warehouseAddressLine2"
                            form="projectDetailsForm"
                            defaultValue={activeProject.warehouseAddressLine2} 
                            placeholder="Baris 2 (Pilihan)"
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input 
                                name="warehousePostcode"
                                form="projectDetailsForm"
                                defaultValue={activeProject.warehousePostcode} 
                                placeholder="Poskod"
                            />
                            <Select 
                                name="warehouseState"
                                form="projectDetailsForm"
                                defaultValue={activeProject.warehouseState || 'W.P. Kuala Lumpur'}
                            >
                                <option value="Johor">Johor</option>
                                <option value="Kedah">Kedah</option>
                                <option value="Kelantan">Kelantan</option>
                                <option value="Melaka">Melaka</option>
                                <option value="Negeri Sembilan">Negeri Sembilan</option>
                                <option value="Pahang">Pahang</option>
                                <option value="Penang">Penang</option>
                                <option value="Perak">Perak</option>
                                <option value="Perlis">Perlis</option>
                                <option value="Sabah">Sabah</option>
                                <option value="Sarawak">Sarawak</option>
                                <option value="Selangor">Selangor</option>
                                <option value="Terengganu">Terengganu</option>
                                <option value="W.P. Kuala Lumpur">W.P. Kuala Lumpur</option>
                                <option value="W.P. Labuan">W.P. Labuan</option>
                                <option value="W.P. Putrajaya">W.P. Putrajaya</option>
                            </Select>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Identiti Projek (Project Identity) */}
            <Card>
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-slate-800 mb-1">Identiti Projek</h3>
                    <p className="text-sm text-slate-500">Nama projek dan ikon untuk dashboard.</p>
                </div>
                <div className="space-y-6">
                    <Input 
                        label="NAMA PROJEK" 
                        name="name"
                        form="projectDetailsForm"
                        defaultValue={activeProject.name} 
                        placeholder="Demo: Urban Cafe Bangsar"
                        required
                    />
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">PENERANGAN</label>
                        <textarea 
                            name="description"
                            form="projectDetailsForm"
                            defaultValue={activeProject.description}
                            placeholder="Inventori untuk cawangan cafe premium."
                            rows={3}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all resize-none"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">IKON PROJEK</label>
                        <p className="text-xs text-slate-500 mb-4">Pilih ikon yang mewakili projek anda. Ikon ini akan dipaparkan di halaman utama.</p>
                        <input type="hidden" name="icon" form="projectDetailsForm" value={selectedProjectIcon} />
                        <div className="grid grid-cols-4 sm:grid-cols-8 gap-3">
                            {[
                                { name: 'Package', component: Package },
                                { name: 'Store', component: Store },
                                { name: 'ShoppingBag', component: ShoppingBag },
                                { name: 'Briefcase', component: Briefcase },
                                { name: 'Factory', component: Factory },
                                { name: 'Coffee', component: Coffee },
                                { name: 'Utensils', component: Utensils },
                                { name: 'Wrench', component: Wrench },
                                { name: 'Hammer', component: Hammer },
                                { name: 'Globe', component: Globe },
                                { name: 'Truck', component: Truck },
                                { name: 'Home', component: Home },
                                { name: 'Anchor', component: Anchor },
                                { name: 'Zap', component: Zap },
                                { name: 'Crown', component: Crown },
                                { name: 'Camera', component: Camera }
                            ].map((icon) => {
                                const IconComponent = icon.component;
                                const isSelected = selectedProjectIcon === icon.name;
                                return (
                                    <button
                                        key={icon.name}
                                        type="button"
                                        onClick={() => setSelectedProjectIcon(icon.name)}
                                        className={`
                                            w-full aspect-square rounded-xl flex items-center justify-center transition-all
                                            ${isSelected 
                                                ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 scale-105' 
                                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600 border border-slate-200'
                                            }
                                        `}
                                    >
                                        <IconComponent size={24} />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Save and Delete Buttons */}
            <div className="flex justify-between items-center pt-4">
                {can.deleteProject && (
                    <Button 
                        variant="danger"
                        onClick={() => {
                            setIsDeleteProjectModalOpen(true);
                            setDeleteProjectPassword('');
                            setDeleteProjectError('');
                        }}
                        icon={Trash2}
                    >
                        Hapus Projek
                    </Button>
                )}
                {!can.deleteProject && <div></div>}
                <Button 
                    onClick={() => {
                        requestConfirm(
                            "Simpan Perubahan", 
                            "Adakah anda pasti mahu menyimpan butiran projek ini?", 
                            async () => {
                                const form = document.getElementById('projectDetailsForm');
                                const formData = new FormData(form);
                                const updates = {};
                                for (let [key, value] of formData.entries()) {
                                    updates[key] = value;
                                }
                                try {
                                    await db.updateProject(activeProject.id, updates);
                                    fetchData();
                                    showToast("Butiran projek berjaya dikemaskini");
                                } catch (err) {
                                    showToast("Ralat menyimpan butiran projek", "error");
                                }
                            }
                        );
                    }}
                    icon={Save}
                >
                    Simpan Butiran Projek
                </Button>
            </div>
        </div>
    );
});

ProjectDetails.displayName = 'ProjectDetails';

export default ProjectDetails;
